import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../services/store';
import { AnalyticsInitialState, Index } from '../types/analysis';

const initialSummary = {
  regDate: '',
  today: '',
  total: {
    startDate: '',
    endDate: '',
    studentCount: 0,
    totalTime: '',
  },
  timeZone: {
    best: '',
    timeZone: [],
  },
  weekZone: {
    best: '',
    timeZone: [],
  },
  daily: { min: 0, max: 0, avg: 0, classAvg: 0, totalAvg: 0, studentCount: 0 },
  attention: {
    total: '',
    time: '',
    classAvg: '',
    totalAvg: '',
    studentCount: 0,
  },
  note: {
    total: '',
    time: '',
    classAvg: '',
    totalAvg: '',
    studentCount: 0,
    title: '',
  },
};

export const initialState: AnalyticsInitialState = {
  myActivity: {
    summary: { alias: '', ...initialSummary },
    libraryType: 'library',
    projectList: [],
    projectId: 0,
    activityList: [],
  },
  classActivity: {
    initialValues: {
      commonType: {
        schoolTypeList: [],
        gradeTypeList: [],
      },
      groupList: [],
    },
    options: {
      schoolType: [],
      gradeType: [],
      search: '',
      seq: [],
      startDate: null,
      endDate: null,
    },
    summary: initialSummary,
    classStudentList: [],
  },
  groupActivity: {
    initialValues: {
      commonType: {
        schoolTypeList: [],
        gradeTypeList: [],
      },
      groupList: [],
    },
    options: {
      search: '',
      seq: [],
      startDate: null,
      endDate: null,
      sortBy: 'last_date',
      sortDir: 'DESC',
    },
    summary: initialSummary,
    projectList: [],
    projectId: 0,
    projectStudentList: [],
  },
  studentActivity: {
    userId: 0,
    studentMgmt: {
      dispUserStatus: '',
      dispMgmtStatus: '',
      dispRequestDate: '',
      dispApprovalDate: '',
      email: '',
      nm: '',
      alias: '',
      schoolTypeCd: '',
      schoolNm: '',
      gradeTypeCd: '',
      departmentTypeCd: '',
    },
    studentAnalysis: initialSummary,
    projectList: [],
    projectId: 0,
    activityList: [],
  },
  projectIndexList: [],
  quizList: [],
  quizTotalCorrect: '',
  quizListPage: 0,
  quiz: {
    seq: null,
  },
};

export const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    mySummaryChanged: (state, { payload }) => {
      state.myActivity.summary = payload;
    },
    mySummaryDeleted: (state) => {
      state.myActivity.summary = initialState.myActivity.summary;
    },
    myProjectLibraryChanged: (state, { payload }) => {
      state.myActivity.libraryType = payload;
    },
    myProjectListChanged: (state, { payload }) => {
      state.myActivity.projectList = payload;
    },
    myProjectIdChanged: (state, { payload }) => {
      state.myActivity.projectId = payload;
    },
    myProjectActivityListChanged: (state, { payload }) => {
      state.myActivity.activityList = payload;
    },
    classOptionsChanged: (state, { payload }) => {
      state.classActivity.options = {
        ...state.classActivity.options,
        ...payload,
      };
    },
    classSummaryChanged: (state, { payload }) => {
      state.classActivity.summary = payload;
    },
    classStudentListChanged: (state, { payload }) => {
      state.classActivity.classStudentList = payload;
    },
    classSummaryDisabled: (state) => {
      state.classActivity.summary = initialState.classActivity.summary;
    },
    classStudentListReset: (state) => {
      state.classActivity.classStudentList =
        initialState.classActivity.classStudentList;
    },
    groupSummaryChanged: (state, { payload }) => {
      state.groupActivity.summary = payload;
    },
    groupProjectListChanged: (state, { payload }) => {
      state.groupActivity.projectList = payload;
    },
    groupOptionsChanged: (state, { payload }) => {
      state.groupActivity.options = {
        ...state.groupActivity.options,
        ...payload,
      };
    },
    groupSummaryDisabled: (state, { payload }) => {
      state.groupActivity.summary = {
        ...initialState.groupActivity.summary,
        ...payload,
      };
    },
    groupProjectIdChanged: (state, { payload }) => {
      state.groupActivity.projectId = payload;
    },
    projectStudentListChanged: (state, { payload }) => {
      state.groupActivity.projectStudentList = payload;
    },
    studentUserIdChanged: (state, { payload }) => {
      state.studentActivity.userId = payload;
    },
    studentAnalysisChanged: (state, { payload }) => {
      state.studentActivity.studentAnalysis = payload;
    },
    studentProjectListChanged: (state, { payload }) => {
      state.studentActivity.projectList = payload;
    },
    studentProjectIdChanged: (state, { payload }) => {
      state.studentActivity.projectId = payload;
    },
    studentProjectActivityListChanged: (state, { payload }) => {
      state.studentActivity.activityList = payload;
    },
    projectIndexListChanged: (state, { payload }) => {
      state.projectIndexList = payload.filter(({ indexYn }: Index) => !indexYn);
    },
    quizListChanged: (state, { payload }) => {
      state.quizList = payload;
    },
    quizTotalCorrectChanged: (state, { payload }) => {
      state.quizTotalCorrect = payload;
    },
    quizListPageChanged: (state, { payload }) => {
      state.quizListPage = payload;
    },
    quizChanged: (state, { payload }) => {
      state.quiz = { ...state.quiz, ...payload };
    },
  },
});

export const {
  mySummaryChanged,
  mySummaryDeleted,
  myProjectLibraryChanged,
  myProjectListChanged,
  myProjectIdChanged,
  myProjectActivityListChanged,
  projectIndexListChanged,
  classOptionsChanged,
  classSummaryChanged,
  classStudentListChanged,
  classSummaryDisabled,
  classStudentListReset,
  groupSummaryChanged,
  groupProjectListChanged,
  groupOptionsChanged,
  groupSummaryDisabled,
  groupProjectIdChanged,
  projectStudentListChanged,
  studentUserIdChanged,
  studentAnalysisChanged,
  studentProjectListChanged,
  studentProjectIdChanged,
  studentProjectActivityListChanged,
  quizListChanged,
  quizListPageChanged,
  quizTotalCorrectChanged,
  quizChanged,
} = analysisSlice.actions;

export const selectMySummary = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.myActivity.summary
);

export const selectMyProjectLibrary = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.myActivity.libraryType
);

export const selectMyProjectList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.myActivity.projectList
);

export const selectMyProjectById = (projectId: number) =>
  createSelector(
    selectMyProjectList,
    (projectList) =>
      projectList.filter(({ projectId: id }) => id === projectId)[0]
  );

export const selectMyProjectId = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.myActivity.projectId
);

export const selectMyProjectActivityList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.myActivity.activityList
);

export const selectClassOptions = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.classActivity.options
);

export const selectClassSummary = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.classActivity.summary
);

export const selectClassStudentList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.classActivity.classStudentList
);

export const selectGroupOptions = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.groupActivity.options
);

export const selectGroupSummary = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.groupActivity.summary
);

export const selectProjectStudentList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.groupActivity.projectStudentList
);

export const selectGroupProjectId = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.groupActivity.projectId
);

export const selectStudentUserId = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.studentActivity.userId
);

export const selectStudentSummary = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.studentActivity.studentAnalysis
);

export const selectStudentProjectList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.studentActivity.projectList
);

export const selectStudentProjectId = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.studentActivity.projectId
);

export const selectStudentActivityList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.studentActivity.activityList
);

export const selectStudentProjectById = (projectId: number) =>
  createSelector(
    selectStudentProjectList,
    (projectList) =>
      projectList.filter(({ projectId: id }) => id === projectId)[0]
  );

export const selectProjectIndexList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.projectIndexList
);

export const selectQuizList = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.quizList
);

export const selectQuizTotalCorrect = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.quizTotalCorrect
);

export const selectQuizListPage = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.quizListPage
);

export const selectQuiz = createSelector(
  (state: RootState) => state.analysis,
  (analysis) => analysis.quiz
);

export default analysisSlice.reducer;
